<template>
  <section v-if="steps.length > 0" class="issue-timeline">
    <h2 class="issue-timeline__header">{{ t('collection.timeline.header') }}</h2>

    <!-- A real <ol>: the history IS ordered, and screen readers announce the
         count + position, which a stack of divs wouldn't. -->
    <ol class="issue-timeline__list">
      <li
        v-for="(step, index) in steps"
        :key="`${step.title}-${step.at}-${index}`"
        class="issue-timeline__step"
        :class="[
          `issue-timeline__step--${step.actor}`,
          {
            'issue-timeline__step--terminal': step.isTerminal,
            'issue-timeline__step--neutral-terminal': step.isNeutralTerminal,
          },
        ]"
      >
        <span class="issue-timeline__rail" aria-hidden="true">
          <span class="issue-timeline__dot" />
        </span>

        <div class="issue-timeline__body">
          <!-- The channel rides in the SAME <p> as the title, not a sibling
               node: "you replied (by phone)" is one sentence, and splitting it
               would make a screen reader announce two unrelated fragments. -->
          <p class="issue-timeline__title">
            {{ t(step.titleKey)
            }}<span v-if="step.channelKey" class="issue-timeline__channel">
              {{ t(step.channelKey) }}</span>
          </p>
          <time v-if="step.at" class="issue-timeline__at" :datetime="step.at">{{
            formatAt(step.at)
          }}</time>
          <!-- The member's own note on that reply — the only step the backend
               ever attaches a comment to. Quoted back so a member revising an
               answer can see what they told us last time. -->
          <p v-if="step.comment" class="issue-timeline__comment">
            <f7-icon f7="text_quote" size="13" class="issue-timeline__comment-icon"></f7-icon>
            <span>{{ step.comment }}</span>
          </p>
        </div>
      </li>
    </ol>
  </section>
</template>

<script setup>
/**
 * IssueTimeline — the member-safe history of one item-issue, rendered from the
 * backend's `timeline` array (interaction-v2). Purely presentational: the parent
 * passes already-normalized steps (see timelineSteps in constants/collectionIssue),
 * so the fixed English `title` vocabulary is resolved to localized member copy
 * through each step's `titleKey` and never leaks to the screen.
 *
 * `actor` drives the visual side/tint — the member's own steps read as "mine",
 * the leader's and the plan-close automation's as neutral "ours". Meaning never
 * depends on that colour alone: the title text itself says who did what.
 *
 * Two 2026-07-28 additions: a reply may carry the CHANNEL it arrived through
 * (a member who phoned her answer in sees it was recorded), and a withdrawal
 * now renders as a `request-closed` closure instead of being scrubbed — kept
 * visually neutral, because it is terminal without being an outcome.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // Normalized steps: { at, actor, title, titleKey, comment, isTerminal }.
  steps: {
    type: Array,
    default: () => [],
  },
});

const { t, locale } = useI18n({ useScope: 'global' });

// ui-western-digits lock: `-u-nu-latn` forces Latin numerals so the Arabic
// locale never renders Arabic-Indic digits in a timestamp.
const dateFormatter = computed(
  () =>
    new Intl.DateTimeFormat(locale.value === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }),
);

function formatAt(at) {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '';
  return dateFormatter.value.format(date);
}

// Exposed for the parent's aria wiring / tests without re-deriving the array.
defineExpose({ count: computed(() => props.steps.length) });
</script>

<style scoped>
.issue-timeline {
  margin-top: 18px;
}

.issue-timeline__header {
  margin: 0 2px 12px;
  font-size: var(--poolit-font-size-body, 0.95rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--poolit-text-primary, #15201c);
}

.issue-timeline__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.issue-timeline__step {
  display: flex;
  gap: 12px;
  padding-bottom: 16px;
}

.issue-timeline__step:last-child {
  padding-bottom: 0;
}

/* The rail: a dot plus the connector down to the next step. The connector is a
   pseudo-element on the rail (not a border on the step) so the LAST step's line
   simply isn't drawn, instead of hanging past the final dot. */
.issue-timeline__rail {
  position: relative;
  flex-shrink: 0;
  width: 14px;
  display: flex;
  justify-content: center;
  padding-top: 5px;
}

.issue-timeline__step:not(:last-child) .issue-timeline__rail::after {
  content: '';
  position: absolute;
  top: 18px;
  bottom: -16px;
  width: 2px;
  background: var(--ci-border, #dce4e0);
  border-radius: 2px;
}

.issue-timeline__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--ci-muted-bg, #eceef1);
  border: 2px solid var(--ci-border, #c8d2cd);
  box-sizing: content-box;
}

/* The member's own steps — the brand tint reads as "this was me". */
.issue-timeline__step--you .issue-timeline__dot {
  background: var(--ci-brand-fg, #6621e2);
  border-color: var(--ci-brand-bg, #f0eaff);
}

/* The step that ENDED the issue — filled, so the eye lands on the outcome. */
.issue-timeline__step--terminal .issue-timeline__dot {
  background: var(--ci-ok-fg, #16964f);
  border-color: var(--ci-ok-bg, #e1f3e8);
}

/* A closure is terminal but is NOT an outcome — nothing was collected, deferred
   or lost. It keeps the filled "the story ends here" dot but drops the success
   tint, so it reads as neither a win nor a failure. */
.issue-timeline__step--neutral-terminal .issue-timeline__dot {
  background: var(--poolit-text-muted, #66746d);
  border-color: var(--ci-border, #dce4e0);
}

.issue-timeline__body {
  flex: 1;
  min-width: 0;
}

.issue-timeline__title {
  margin: 0;
  font-size: var(--poolit-font-size-body, 0.92rem);
  font-weight: var(--poolit-font-weight-semibold, 600);
  line-height: 1.5;
  color: var(--poolit-text-primary, #15201c);
}

.issue-timeline__step--terminal .issue-timeline__title {
  color: var(--ci-ok-fg, #16964f);
}

.issue-timeline__step--neutral-terminal .issue-timeline__title {
  color: var(--poolit-text-primary, #15201c);
}

/* The channel qualifier is secondary to the reply itself — quieter weight and
   colour, but still inside the title line so it reads as one phrase. */
.issue-timeline__channel {
  font-weight: var(--poolit-font-weight-regular, 400);
  color: var(--poolit-text-muted, #66746d);
}

.issue-timeline__at {
  display: block;
  margin-top: 2px;
  font-size: var(--poolit-font-size-small, 0.76rem);
  color: var(--poolit-text-muted, #66746d);
  font-variant-numeric: tabular-nums;
}

.issue-timeline__comment {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 8px 0 0;
  padding: 8px 11px;
  border-radius: var(--poolit-radius-md, 10px);
  background: var(--ci-surface-soft, #eef2f0);
  font-size: var(--poolit-font-size-small, 0.84rem);
  line-height: 1.6;
  color: var(--poolit-text-secondary, #3d4a44);
}

.issue-timeline__comment-icon {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--poolit-text-placeholder, #9ca3af);
}
</style>
